// src/pages/UykuTakipDetay.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, getDoc, serverTimestamp, Timestamp, deleteDoc } from 'firebase/firestore'; // deleteDoc eklendi
import useCustomerOptions from '../hooks/useCustomerOptions';
import { useNavigate, useParams } from 'react-router-dom'; // Yönlendirme ve parametreler
import { Button, TextField, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, InputAdornment, Autocomplete, Grid, Typography, IconButton } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete'; // Delete icon
import { Loading } from '../components/common/Loading';

import GlassCard from '../ui/glass/components/GlassCard';
import usePermissions from '../hooks/usePermissions'; // Yetki kontrolü

const UykuTakipDetay = () => {
    const { id } = useParams(); // URL'den ID al
    const navigate = useNavigate();
    const { canDelete } = usePermissions(); // Yetki

    // Müşteri listesi: paylaşımlı oturum cache'i (sayfa açılışında tam koleksiyon okuması yok).
    const { customers } = useCustomerOptions();
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerInputValue, setCustomerInputValue] = useState('');
    const [randevuTarihi, setRandevuTarihi] = useState(null);
    const [randevuSaati, setRandevuSaati] = useState(null);
    const [notlar, setNotlar] = useState('');
    const [smsGonder, setSmsGonder] = useState(true);
    const [durum, setDurum] = useState('Planlandı');

    // Yeni alanlar
    const [fiyat, setFiyat] = useState('');
    const [doktoru, setDoktoru] = useState('');
    const [hastane, setHastane] = useState('');
    const [telefon, setTelefon] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [loadingData, setLoadingData] = useState(!!id); // ID varsa loading true


    // Mevcut kaydı çek (Edit mode)
    useEffect(() => {
        const fetchAppointment = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, "sleepAppointments", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();

                    // Müşteriyi bul ve set et (customers yüklendikten sonra veya id ile)
                    // Not: customers array'i asenkron geldiği için burada direkt eşleşmeyebilir.
                    // Ancak customerId var, customers listesi gelince useEffect ile eşleştirme yapılabilir.
                    // Pratik çözüm: Veriyi state'e at, customer eşleşmesini müşteriler gelince yap.

                    setCustomerInputValue(data.musteriAdSoyad || '');
                    setRandevuTarihi(data.randevuTarihi ? data.randevuTarihi.toDate() : null);
                    setRandevuSaati(data.randevuTarihi ? data.randevuTarihi.toDate() : null);
                    setNotlar(data.notlar || '');
                    setSmsGonder(data.smsGonder !== false);
                    setDurum(data.durum || 'Planlandı');
                    setFiyat(data.fiyat || '');
                    setDoktoru(data.doktoru || '');
                    setHastane(data.hastane || '');
                    setTelefon(data.musteriTelefon || '');

                    // Seçili müşteri ref
                    // Customers state'i doluysa bul
                } else {
                    alert("Kayıt bulunamadı!");
                    navigate('/uyku-takip');
                }
            } catch (error) {
                console.error("Veri çekme hatası:", error);
            } finally {
                setLoadingData(false);
            }
        };

        fetchAppointment();
    }, [id, navigate]);

    // Customers yüklendiğinde ve data geldiğinde eşleştirme
    useEffect(() => {
        if (id && customers.length > 0 && customerInputValue && !selectedCustomer) {
            const found = customers.find(c => c.adSoyad === customerInputValue); // İsime göre eşleştirme (riskli ama id yoksa mecbur)
            // Aslında data.musteriId var mı? Evet. Fetch kısmında state'e atmadık.
            // En temizi: fetchAppointment içinde datayı alıp customers içinde aramak.
            // Ama customers henüz yüklenmemiş olabilir.
            // Basitlik adına: Edit modunda müşteri adını input value olarak gösteriyoruz zaten.
        }
    }, [customers, id, customerInputValue, selectedCustomer]);

    // Daha iyi bir eşleştirme: Kaydı çekerken musteriId'yi bir state'e atıp, customers değiştikçe kontrol etmek.
    // Şimdilik existing mantığı koruyoruz: customerInputValue dolu geliyor.

    const handleCustomerChange = (event, newValue) => {
        setSelectedCustomer(newValue);
        if (newValue) {
            setCustomerInputValue(newValue.adSoyad);
            if (newValue.telefon) {
                setTelefon(newValue.telefon);
            }
        } else {
            setCustomerInputValue('');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // Validasyon
        if (!customerInputValue || !randevuTarihi || !randevuSaati) {
            alert('Hasta Adı, Tarih ve Saat alanları zorunludur.');
            return;
        }

        setIsSaving(true);

        const finalDate = new Date(randevuTarihi);
        finalDate.setHours(randevuSaati.getHours());
        finalDate.setMinutes(randevuSaati.getMinutes());

        let musteriId = selectedCustomer?.id || null;
        // Eğer edit moddaysa ve müşteri değişmediyse eski musteriId korunmalı (fakat burada state yok)
        // Yeni müşteri ekleme mantığı Modal'daki ile aynı

        // Eğer müşteri veritabanında yoksa ve yeni girildiyse
        if (!selectedCustomer && customerInputValue) {
            // Mevcut customers içinde ara
            const existing = customers.find(c => c.adSoyad.toLowerCase() === customerInputValue.toLowerCase());
            if (existing) {
                musteriId = existing.id;
            } else {
                // Yeni müşteri oluştur
                try {
                    const newCustomerRef = await addDoc(collection(db, "customers"), {
                        adSoyad: customerInputValue,
                        telefon: telefon,
                        hizmetTuru: 'Uyku Hastası',
                        eklenmeTarihi: serverTimestamp()
                    });
                    musteriId = newCustomerRef.id;
                } catch (error) {
                    console.error("Yeni Müşteri Hatası:", error);
                    alert("Yeni müşteri oluşturulamadı.");
                    setIsSaving(false);
                    return;
                }
            }
        }

        const appointmentData = {
            musteriId,
            musteriAdSoyad: customerInputValue,
            musteriTelefon: telefon,
            randevuTarihi: Timestamp.fromDate(finalDate),
            notlar,
            smsGonder,
            durum,
            fiyat: fiyat ? parseFloat(fiyat) : 0,
            doktoru,
            hastane
        };

        try {
            if (id) {
                await updateDoc(doc(db, "sleepAppointments", id), appointmentData);
            } else {
                await addDoc(collection(db, "sleepAppointments"), {
                    ...appointmentData,
                    olusturmaTarihi: serverTimestamp()
                });
            }
            navigate('/uyku-takip');
        } catch (error) {
            console.error("Kaydetme hatası:", error);
            alert("Kaydedilirken bir hata oluştu: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        if (window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "sleepAppointments", id));
                navigate('/uyku-takip');
            } catch (error) {
                console.error("Silme hatası:", error);
                alert("Silinirken hata oluştu.");
            }
        }
    };

    if (loadingData) {
        return <Loading message="Yükleniyor..." />;
    }

    return (
        <Box sx={{ p: 1 }}>
            <GlassCard sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/uyku-takip')} sx={{ color: 'text.secondary' }}>
                            Geri
                        </Button>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {id ? 'Randevu Detayı / Düzenle' : 'Yeni Uyku Randevusu'}
                        </Typography>
                    </Box>
                    {id && canDelete() && (
                        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                            Sil
                        </Button>
                    )}
                </Box>

                <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                freeSolo
                                options={customers}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.adSoyad}
                                value={selectedCustomer || (customerInputValue ? { adSoyad: customerInputValue } : null)}
                                onChange={handleCustomerChange}
                                inputValue={customerInputValue}
                                onInputChange={(event, newInputValue) => setCustomerInputValue(newInputValue)}
                                renderInput={(params) => <TextField {...params} label="Hasta Adı Soyadı" required />}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Telefon"
                                value={telefon}
                                onChange={(e) => setTelefon(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePicker label="Randevu Tarihi" value={randevuTarihi} onChange={setRandevuTarihi} sx={{ width: '100%' }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TimePicker label="Randevu Saati" value={randevuSaati} onChange={setRandevuSaati} sx={{ width: '100%' }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Fiyat"
                                value={fiyat}
                                onChange={(e) => setFiyat(e.target.value)}
                                type="number"
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₺</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Durum</InputLabel>
                                <Select value={durum} label="Durum" onChange={(e) => setDurum(e.target.value)}>
                                    <MenuItem value="Planlandı">Planlandı</MenuItem>
                                    <MenuItem value="Tamamlandı">Tamamlandı</MenuItem>
                                    <MenuItem value="İptal Edildi">İptal Edildi</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Doktoru"
                                value={doktoru}
                                onChange={(e) => setDoktoru(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Hastane"
                                value={hastane}
                                onChange={(e) => setHastane(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Notlar" value={notlar} onChange={(e) => setNotlar(e.target.value)} multiline rows={3} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Checkbox checked={smsGonder} onChange={(e) => setSmsGonder(e.target.checked)} />} label="Müşteriye SMS Gönder" />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button variant="outlined" onClick={() => navigate('/uyku-takip')}>İptal</Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                    </Box>
                </Box>
            </GlassCard>
        </Box>
    );
};

export default UykuTakipDetay;
